import { captureException } from "@sentry/react";
import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { css } from "../../../styled-system/css";
import bousaiMiyapo from "../../assets/miyagi/bousai-miyapo.png";
import Header from "../../components/header";

import {
  HOKKAIDO_HOUSEHOLDER_CAMPAIGN_END_DATE,
  KESENNUMA_AGE_CUTOFF_DATE,
  MUNICIPAL_CAMPAIGN_REGIONS,
  R8_CAMPAIGN_CONFIGS,
  TOME_TARGET_BIRTHDAY_FROM,
  TOME_TARGET_BIRTHDAY_TO,
} from "../../config";
import { ChargeCompleteModal } from "../../features/charge/charge-complete-modal";
import {
  canUseOsakiChargeBonusCharge,
  getCurrentOsakiChargeBonusCampaignPeriod,
  getCurrentOsakiChargeBonusCampaignStatus,
  getOsakiChargeBonusCampaignBannerType,
} from "../../features/charge/osaki-charge-bonus-campaign";
import {
  DistributionCodeModal,
  HokkaidoCampaignInfoCard,
  InvitationEndedBanner,
  InvitationLinkCard,
  KesennumaLotteryModal,
  KyotoRepresentativeCampaignBanner,
  KyotoRepresentativeCampaignCompleteModal,
  LotteryModal,
  MiyagiBousaiWelcomeLotteryModal,
  MiyagiMunicipalCampaignBanner,
  MiyagiResidenceCardModal,
  MiyagiResidenceCardCapabilityModal,
  OsakiChargeBonusCampaignBanner,
  OsakiReregistrationModal,
  R7MarumoriCampaignBanner,
  R7MiyagiCampaignBanner,
  R7ShigaOtsuCampaignBanner,
  R8CampaignBanner,
  ShiroishiLotteryModal,
  StandaloneDistributionCodeModal,
  WalletCard,
  ensureDigitalSignatureCertificate,
  getBalanceInfo,
  getInvitationStatus,
  getR7MarumoriCampaignBannerType,
  getR7MiyagiCampaignBannerType,
  getR7ShigaOtsuCampaignBannerType,
  getR8CampaignBannerType,
  getR8MiyagiBousaiWelcomeCampaignBannerType,
  getR8MiyagiResidenceCardCampaignBannerType,
  requiresDigitalSignatureAt,
} from "../../features/home";
import { TransferInInitiatedModal } from "../../features/home/components/transfer-in-initiated-modal";
import { HokkaidoMynaApplicationHomeBanner } from "../../features/hokkaido-myna-application/components";
import {
  type HokkaidoMynaApplicationBannerType,
  getHokkaidoMynaApplicationBannerType,
  isHokkaidoMynaApplicationHomeVisible,
} from "../../features/hokkaido-myna-application/state";
import { canShowKyotoRepresentativeCampaign } from "../../features/representative-campaign";

import { getCurrentTime } from "../../libs/date";
import { AppError, ERROR_CODES } from "../../libs/errors";
import { t } from "../../libs/i18n";
import { clearPendingModal, getPendingModal } from "../../libs/pending-modal";
import { getQueryClient } from "../../libs/query-client";
import {
  checkDigitalSignatureCertificate,
  getBirthday,
  getCityAddressWithCache,
  getCityAddressWithUpdatedAt,
  getResidenceCardCapability,
} from "../../libs/sdk";
import type { ResidenceCardCapabilityResult } from "../../libs/sdk";
import { getAppEnv } from "../../libs/utils";

export const Route = createFileRoute("/_authed/")({
  loader: async ({ location, context: { client, tenant } }) => {
    const currentTime = await getCurrentTime();
    const balance = await client.getMyBalances({}).catch((error) => {
      throw new AppError(
        ERROR_CODES.CONNECT_GET_MY_BALANCES_FAILED,
        error.message,
        {
          originalError: error,
        },
      );
    });

    const { totalAmount, nearestExpireAtDate } = getBalanceInfo(balance);

    const invitationStatus = await getInvitationStatus({
      tenant,
      client,
    });

    // R7キャンペーンステータスを取得（対応テナント専用）
    let campaignStatuses = null;
    if (["miyagi", "marumori", "shiga-otsu"].includes(tenant.slug)) {
      campaignStatuses = await client
        .getMyCampaignStatuses({})
        .catch((error) => {
          throw new AppError(
            ERROR_CODES.CONNECT_GET_MY_CAMPAIGN_STATUSES_FAILED,
            error.message,
            {
              originalError: error,
            },
          );
        });
    }

    // 市区町村情報を取得
    const cityAddress = (await getCityAddressWithCache()) ?? "";
    const showKyotoRepresentativeCampaign = canShowKyotoRepresentativeCampaign({
      tenantSlug: tenant.slug,
      cityAddress,
      currentTime: currentTime.date,
      appEnv: getAppEnv(),
    });

    // R8物価高騰対策キャンペーン判定（miyagiテナントのみ）
    let isOsakiResident = false;
    let isSendaiResident = false;
    let isShiroishiResident = false;
    let isTagajoResident = false;
    let isKesennumaResident = false;
    let isTomeTargetResident = false;
    let isMiyagiResident = false;
    let isKesennumaAgeEligible = true;
    let canApplyR8Campaign = false;
    let residenceCardCapability: ResidenceCardCapabilityResult = "unavailable";
    let residenceCardRequiresDigitalSignature = false;
    let canApplyResidenceCardCampaign = false;
    if (tenant.slug === "miyagi") {
      const { cityAddress: cityAddressWithTime, updatedAt } =
        await getCityAddressWithUpdatedAt();
      isMiyagiResident = cityAddressWithTime?.startsWith("宮城県") ?? false;
      isOsakiResident =
        cityAddressWithTime?.startsWith("宮城県大崎市") ?? false;
      isSendaiResident =
        cityAddressWithTime?.startsWith("宮城県仙台市") ?? false;
      isShiroishiResident =
        cityAddressWithTime?.startsWith("宮城県白石市") ?? false;
      isTagajoResident =
        cityAddressWithTime?.startsWith("宮城県多賀城市") ?? false;
      isKesennumaResident =
        cityAddressWithTime?.startsWith("宮城県気仙沼市") ?? false;
      const isTomeResident =
        cityAddressWithTime?.startsWith("宮城県登米市") ?? false;

      // 気仙沼市住民の場合、年齢制限を判定
      if (isKesennumaResident) {
        const birthday = await getBirthday();
        if (birthday) {
          // 平成19年4月1日以前生まれ（19歳以上）のみ対象
          isKesennumaAgeEligible = birthday <= KESENNUMA_AGE_CUTOFF_DATE;
        }
      }

      // 登米市は対象児童（平成20年4月2日〜令和8年11月30日生まれ）以外にはバナー自体を表示しない
      if (isTomeResident) {
        const birthday = await getBirthday();
        if (birthday) {
          isTomeTargetResident =
            birthday >= TOME_TARGET_BIRTHDAY_FROM &&
            birthday <= TOME_TARGET_BIRTHDAY_TO;
        }
      }

      // みやぎ防災ウェルカムポイントは全県対象のため、対象市に限らず判定が必要
      if (isMiyagiResident) {
        // 応募可能判定には実際の時刻を使用する（Mock を使うと動作確認ができないため）
        const realCurrentTime = await getCurrentTime({ ignoreMock: true });
        // prod-live 以外の環境では3分、prod-live では24時間
        const oneDayMs =
          getAppEnv() === "prod/live" ? 24 * 60 * 60 * 1000 : 3 * 60 * 1000;
        const isUpdatedWithinOneDay =
          updatedAt !== null &&
          realCurrentTime.date.getTime() - updatedAt.getTime() < oneDayMs;
        // 搭載状況を取得でき、未搭載と判定されていない人に
        // ようこそみやぎ外国人ポイントのバナーを出す。期間外は判定に使わないので読まない
        const residenceCardConfig = R8_CAMPAIGN_CONFIGS.miyagiResidenceCard;
        const isResidenceCardCampaignOpen =
          currentTime.source !== "client" &&
          currentTime.date >= residenceCardConfig.startDate &&
          currentTime.date <= residenceCardConfig.endDate;

        const [isCertificateValid, capability] = await Promise.all([
          checkDigitalSignatureCertificate(),
          isResidenceCardCampaignOpen
            ? getResidenceCardCapability()
            : Promise.resolve("unavailable" as const),
        ]);
        canApplyR8Campaign = isUpdatedWithinOneDay || isCertificateValid;
        residenceCardCapability = capability;
        if (
          isResidenceCardCampaignOpen &&
          residenceCardConfig.requiresDigitalSignature
        ) {
          residenceCardRequiresDigitalSignature = requiresDigitalSignatureAt(
            await getBirthday(),
            currentTime.date,
          );
        }
        // 署名用を求める人は、搭載済みと分かり署名用で登録済みになるまで「準備する」を押してもらう
        canApplyResidenceCardCampaign = residenceCardRequiresDigitalSignature
          ? capability === "present" && isCertificateValid
          : canApplyR8Campaign;
      }
    }

    // 大崎市キャンペーンバナータイプを取得
    const osakiCampaignBannerType = getR8CampaignBannerType(
      campaignStatuses,
      currentTime,
      isOsakiResident,
      R8_CAMPAIGN_CONFIGS.osaki,
    );

    // 仙台市キャンペーンバナータイプを取得
    const sendaiCampaignBannerType = getR8CampaignBannerType(
      campaignStatuses,
      currentTime,
      isSendaiResident,
      R8_CAMPAIGN_CONFIGS.sendai,
    );

    // 白石市キャンペーンバナータイプを取得
    const shiroishiCampaignBannerType = getR8CampaignBannerType(
      campaignStatuses,
      currentTime,
      isShiroishiResident,
      R8_CAMPAIGN_CONFIGS.shiroishi,
    );

    // 多賀城市キャンペーンバナータイプを取得
    const tagajoCampaignBannerType = getR8CampaignBannerType(
      campaignStatuses,
      currentTime,
      isTagajoResident,
      R8_CAMPAIGN_CONFIGS.tagajo,
    );

    // 気仙沼市キャンペーンバナータイプを取得
    const kesennumaCampaignBannerType = getR8CampaignBannerType(
      campaignStatuses,
      currentTime,
      isKesennumaResident,
      R8_CAMPAIGN_CONFIGS.kesennuma,
    );

    // 登米市キャンペーンバナータイプを取得
    const tomeCampaignBannerType = getR8CampaignBannerType(
      campaignStatuses,
      currentTime,
      isTomeTargetResident,
      R8_CAMPAIGN_CONFIGS.tome,
    );

    const osakiChargeBonusStatus = getCurrentOsakiChargeBonusCampaignStatus(
      campaignStatuses,
      currentTime.date,
    );
    const osakiChargeBonusCampaignPeriod =
      getCurrentOsakiChargeBonusCampaignPeriod(currentTime.date);
    const osakiChargeBonusCampaignBannerType =
      getOsakiChargeBonusCampaignBannerType(
        osakiChargeBonusStatus,
        isOsakiResident,
        currentTime.date,
      );

    // みやぎキャンペーンバナータイプを取得
    const r7MiyagiCampaignBannerType = getR7MiyagiCampaignBannerType(
      campaignStatuses,
      currentTime.date,
      tenant,
      cityAddress,
    );

    const miyagiBousaiWelcomeCampaignBannerType =
      getR8MiyagiBousaiWelcomeCampaignBannerType(
        campaignStatuses,
        currentTime,
        isMiyagiResident,
        R8_CAMPAIGN_CONFIGS.miyagiBousaiWelcome,
      );

    const miyagiResidenceCardCampaignBannerType =
      getR8MiyagiResidenceCardCampaignBannerType(
        campaignStatuses,
        currentTime,
        isMiyagiResident,
        residenceCardCapability,
        R8_CAMPAIGN_CONFIGS.miyagiResidenceCard,
      );

    // まるもりキャンペーンバナータイプを取得
    const r7MarumoriCampaignBannerType = getR7MarumoriCampaignBannerType(
      campaignStatuses,
      currentTime.date,
      tenant,
      cityAddress,
    );

    // 大津市キャンペーンバナータイプを取得
    const r7ShigaOtsuCampaignBannerType = getR7ShigaOtsuCampaignBannerType(
      campaignStatuses,
      currentTime.date,
      tenant,
    );

    const toastState = location.state?.toast;

    if (toastState) {
      // loaderでtoastを表示するが、わずかに遅延させることで
      // Toasterコンポーネントのマウントを待つ
      setTimeout(() => {
        if (toastState.type === "success") {
          toast.success(toastState.message);
        }
        if (toastState.type === "error") {
          toast.error(toastState.message);
        }
      }, 0);
    }

    // 市町村キャンペーン地域のフィルタリング（MiyagiMunicipalCampaignBanner用）
    const municipalCampaignRegions = MUNICIPAL_CAMPAIGN_REGIONS.filter(
      (region) =>
        currentTime.date >= region.startDate &&
        currentTime.date <= region.endDate,
    );

    const showHokkaidoCampaignInfoCard =
      tenant.slug === "hokkaido" &&
      currentTime.date <= HOKKAIDO_HOUSEHOLDER_CAMPAIGN_END_DATE;

    let hokkaidoMynaApplicationBannerType: HokkaidoMynaApplicationBannerType =
      "hidden";
    if (isHokkaidoMynaApplicationHomeVisible(tenant.slug, currentTime.date)) {
      // 案内バナーのためだけにホーム全体を落とさない
      const application = await client
        .getHokkaidoR8HouseholderMynaApplication({})
        .catch((error) => {
          const appError = new AppError(
            ERROR_CODES.CONNECT_GET_HOKKAIDO_R8_HOUSEHOLDER_MYNA_APPLICATION_FAILED,
            error instanceof Error ? error.message : undefined,
            { originalError: error },
          );
          if (!appError.skipSentry) {
            captureException(appError);
          }
          return null;
        });
      hokkaidoMynaApplicationBannerType =
        getHokkaidoMynaApplicationBannerType(application);
    }

    return {
      totalAmount,
      nearestExpireAtDate,
      tenant,
      showHokkaidoCampaignInfoCard,
      hokkaidoMynaApplicationBannerType,
      showInvitationLink:
        tenant.feature.invitation && !invitationStatus.isInvitationCampaignOver,
      isInvitationCampaignOver: invitationStatus.isInvitationCampaignOver,
      r7MiyagiCampaignBannerType,
      miyagiBousaiWelcomeCampaignBannerType,
      miyagiResidenceCardCampaignBannerType,
      residenceCardCapability,
      residenceCardRequiresDigitalSignature,
      canApplyResidenceCardCampaign,
      r7MarumoriCampaignBannerType,
      r7ShigaOtsuCampaignBannerType,
      osakiCampaignBannerType,
      sendaiCampaignBannerType,
      shiroishiCampaignBannerType,
      tagajoCampaignBannerType,
      kesennumaCampaignBannerType,
      tomeCampaignBannerType,
      osakiChargeBonusCampaignBannerType,
      osakiChargeBonusCampaignTerm:
        osakiChargeBonusCampaignPeriod?.term ?? "1st",
      osakiChargeBonusAppliedQuantity: osakiChargeBonusStatus?.isApplied
        ? Number(osakiChargeBonusStatus.maxQuantity)
        : undefined,
      canUseOsakiChargeBonusCharge: canUseOsakiChargeBonusCharge(
        osakiChargeBonusStatus,
        currentTime.date,
      ),
      isKesennumaAgeEligible,
      canApplyR8Campaign,
      municipalCampaignRegions,
      showKyotoRepresentativeCampaign,
    };
  },
  component: Home,
});

function Home() {
  const {
    totalAmount,
    nearestExpireAtDate,
    tenant,
    showHokkaidoCampaignInfoCard,
    hokkaidoMynaApplicationBannerType,
    showInvitationLink,
    isInvitationCampaignOver,
    r7MiyagiCampaignBannerType,
    miyagiBousaiWelcomeCampaignBannerType,
    miyagiResidenceCardCampaignBannerType,
    residenceCardCapability,
    residenceCardRequiresDigitalSignature,
    canApplyResidenceCardCampaign,
    r7MarumoriCampaignBannerType,
    r7ShigaOtsuCampaignBannerType,
    osakiCampaignBannerType,
    sendaiCampaignBannerType,
    shiroishiCampaignBannerType,
    tagajoCampaignBannerType,
    kesennumaCampaignBannerType,
    tomeCampaignBannerType,
    osakiChargeBonusCampaignBannerType,
    osakiChargeBonusCampaignTerm,
    osakiChargeBonusAppliedQuantity,
    canUseOsakiChargeBonusCharge,
    isKesennumaAgeEligible,
    canApplyR8Campaign,
    municipalCampaignRegions,
    showKyotoRepresentativeCampaign,
  } = Route.useLoaderData();
  const [showModal, setShowModal] = useState(false);
  const [showOsakiPrepareModal, setShowOsakiPrepareModal] = useState(false);
  const [showShiroishiModal, setShowShiroishiModal] = useState(false);
  const [showKesennumaModal, setShowKesennumaModal] = useState(false);
  const [showMiyagiBousaiWelcomeModal, setShowMiyagiBousaiWelcomeModal] =
    useState(false);
  const [showMiyagiResidenceCardModal, setShowMiyagiResidenceCardModal] =
    useState(false);
  const [
    showMiyagiResidenceCardCapabilityModal,
    setShowMiyagiResidenceCardCapabilityModal,
  ] = useState(false);
  const [isCheckingDigitalSignature, setIsCheckingDigitalSignature] =
    useState(false);
  const [modal, setModal] = useState<ModalState | null>(getPendingModal());
  const { client } = Route.useRouteContext();
  const queryClient = getQueryClient();
  const router = useRouter();

  const handleClickPrepareResidenceCard = async () => {
    if (!residenceCardRequiresDigitalSignature) {
      setShowOsakiPrepareModal(true);
      return;
    }
    // 搭載状況が分かるまでは署名用での登録を促さない。対象外の人にまで6桁以上の登録を求めることになるため
    if (residenceCardCapability !== "present") {
      setShowMiyagiResidenceCardCapabilityModal(true);
      return;
    }
    // 現況確認の間に連打されて登録画面が二重に開かないようにする
    if (isCheckingDigitalSignature) {
      return;
    }
    setIsCheckingDigitalSignature(true);
    try {
      const gate = await ensureDigitalSignatureCertificate();
      if (gate.result === "error") {
        toast.error(gate.message);
      } else if (
        gate.result === "ready" ||
        gate.result === "registration-finished"
      ) {
        // 読み直して「ポイントの受け取りへ」を有効にする
        await router.invalidate();
      }
    } finally {
      setIsCheckingDigitalSignature(false);
    }
  };

  const handleClickApplyResidenceCard = () => {
    if (residenceCardCapability !== "present") {
      setShowMiyagiResidenceCardCapabilityModal(true);
      return;
    }
    setShowMiyagiResidenceCardModal(true);
  };

  useEffect(() => {
    // みやポの場合はモーダルの画像をプリロードする
    if (tenant.slug === "miyagi") {
      const img = new Image();
      img.src = bousaiMiyapo;
    }
  }, [tenant.slug]);

  return (
    <>
      <Header title={t("ホーム")} />
      <div
        className={css({
          pt: "24px",
          px: "20px",
          pb: "64px",
          display: "flex",
          flexDir: "column",
          gap: "16px",
        })}
      >
        <WalletCard
          tenant={tenant}
          totalAmount={totalAmount}
          nearestExpireAtDate={nearestExpireAtDate}
          showChargeAction={
            tenant.slug === "miyagi"
              ? canUseOsakiChargeBonusCharge
              : tenant.feature.charge
          }
        />

        <HokkaidoMynaApplicationHomeBanner
          bannerType={hokkaidoMynaApplicationBannerType}
        />

        {showHokkaidoCampaignInfoCard && <HokkaidoCampaignInfoCard />}

        {showInvitationLink && <InvitationLinkCard />}

        {isInvitationCampaignOver && <InvitationEndedBanner />}

        {showKyotoRepresentativeCampaign && (
          <KyotoRepresentativeCampaignBanner />
        )}

        <OsakiChargeBonusCampaignBanner
          bannerType={osakiChargeBonusCampaignBannerType}
          campaignTerm={osakiChargeBonusCampaignTerm}
          canApply={canApplyR8Campaign}
          appliedQuantity={osakiChargeBonusAppliedQuantity}
          onClickPrepare={() => setShowOsakiPrepareModal(true)}
        />

        {osakiCampaignBannerType !== "hidden" && (
          <R8CampaignBanner
            bannerType={osakiCampaignBannerType}
            onClickPrepare={() => setShowOsakiPrepareModal(true)}
            canApply={canApplyR8Campaign}
            config={R8_CAMPAIGN_CONFIGS.osaki}
          />
        )}

        {sendaiCampaignBannerType !== "hidden" && (
          <R8CampaignBanner
            bannerType={sendaiCampaignBannerType}
            onClickPrepare={() => setShowOsakiPrepareModal(true)}
            canApply={canApplyR8Campaign}
            config={R8_CAMPAIGN_CONFIGS.sendai}
          />
        )}

        {shiroishiCampaignBannerType !== "hidden" && (
          <R8CampaignBanner
            bannerType={shiroishiCampaignBannerType}
            onClickPrepare={() => setShowOsakiPrepareModal(true)}
            canApply={canApplyR8Campaign}
            config={R8_CAMPAIGN_CONFIGS.shiroishi}
            onClickApply={() => setShowShiroishiModal(true)}
          />
        )}

        {tagajoCampaignBannerType !== "hidden" && (
          <R8CampaignBanner
            bannerType={tagajoCampaignBannerType}
            onClickPrepare={() => setShowOsakiPrepareModal(true)}
            canApply={canApplyR8Campaign}
            config={R8_CAMPAIGN_CONFIGS.tagajo}
          />
        )}

        {kesennumaCampaignBannerType !== "hidden" && (
          <R8CampaignBanner
            bannerType={kesennumaCampaignBannerType}
            onClickPrepare={() => setShowOsakiPrepareModal(true)}
            canApply={canApplyR8Campaign}
            config={R8_CAMPAIGN_CONFIGS.kesennuma}
            onClickApply={() => setShowKesennumaModal(true)}
            ageRestricted={!isKesennumaAgeEligible}
          />
        )}

        {tomeCampaignBannerType !== "hidden" && (
          <R8CampaignBanner
            bannerType={tomeCampaignBannerType}
            onClickPrepare={() => setShowOsakiPrepareModal(true)}
            canApply={canApplyR8Campaign}
            config={R8_CAMPAIGN_CONFIGS.tome}
          />
        )}

        {tenant.slug === "miyagi" &&
          r7MiyagiCampaignBannerType !== "hidden" && (
            <R7MiyagiCampaignBanner
              bannerType={r7MiyagiCampaignBannerType}
              onClickApply={() => setShowModal(true)}
            />
          )}

        {tenant.slug === "miyagi" &&
          miyagiBousaiWelcomeCampaignBannerType !== "hidden" && (
            <R8CampaignBanner
              bannerType={miyagiBousaiWelcomeCampaignBannerType}
              onClickPrepare={() => setShowOsakiPrepareModal(true)}
              canApply={canApplyR8Campaign}
              config={R8_CAMPAIGN_CONFIGS.miyagiBousaiWelcome}
              onClickApply={() => setShowMiyagiBousaiWelcomeModal(true)}
            />
          )}

        {tenant.slug === "miyagi" &&
          miyagiResidenceCardCampaignBannerType !== "hidden" && (
            <R8CampaignBanner
              bannerType={miyagiResidenceCardCampaignBannerType}
              onClickPrepare={handleClickPrepareResidenceCard}
              canApply={canApplyResidenceCardCampaign}
              config={R8_CAMPAIGN_CONFIGS.miyagiResidenceCard}
              onClickApply={handleClickApplyResidenceCard}
              isPreparing={isCheckingDigitalSignature}
            />
          )}

        {tenant.slug === "marumori" &&
          r7MarumoriCampaignBannerType !== "hidden" && (
            <R7MarumoriCampaignBanner
              bannerType={r7MarumoriCampaignBannerType}
              onClickApply={() => setShowModal(true)}
            />
          )}

        {tenant.slug === "shiga-otsu" &&
          r7ShigaOtsuCampaignBannerType !== "hidden" && (
            <R7ShigaOtsuCampaignBanner
              bannerType={r7ShigaOtsuCampaignBannerType}
              onClickApply={() => setShowModal(true)}
            />
          )}

        {tenant.slug === "miyagi" && municipalCampaignRegions.length > 0 && (
          <MiyagiMunicipalCampaignBanner regions={municipalCampaignRegions} />
        )}
      </div>

      {showModal && (
        <LotteryModal
          onClickCancel={() => setShowModal(false)}
          tenant={tenant}
        />
      )}

      {showOsakiPrepareModal && (
        <OsakiReregistrationModal
          onClose={() => setShowOsakiPrepareModal(false)}
        />
      )}

      {showShiroishiModal && (
        <ShiroishiLotteryModal
          onClickCancel={() => setShowShiroishiModal(false)}
        />
      )}

      {showKesennumaModal && (
        <KesennumaLotteryModal
          onClickCancel={() => setShowKesennumaModal(false)}
        />
      )}

      {showMiyagiBousaiWelcomeModal && (
        <MiyagiBousaiWelcomeLotteryModal
          onClickCancel={() => setShowMiyagiBousaiWelcomeModal(false)}
        />
      )}

      {showMiyagiResidenceCardCapabilityModal && (
        <MiyagiResidenceCardCapabilityModal
          onClose={() => setShowMiyagiResidenceCardCapabilityModal(false)}
        />
      )}

      {showMiyagiResidenceCardModal && (
        <MiyagiResidenceCardModal
          onClickCancel={() => setShowMiyagiResidenceCardModal(false)}
        />
      )}

      {tenant.feature.transfer &&
        modal &&
        modal.type === "transfer-in-initiated" && (
          <TransferInInitiatedModal
            data={modal.data}
            onAccept={async () => {
              await client.acceptTransferCode({
                transferCode: modal.data.transferCode,
              });
              queryClient.resetQueries({
                queryKey: ["transactions"],
              });
              clearPendingModal();
            }}
            onClose={() => {
              clearPendingModal();
              setModal(null);
              router.invalidate();
            }}
          />
        )}

      {modal && modal.type === "distribution-code" && (
        <DistributionCodeModal
          data={modal.data}
          client={client}
          onClose={() => {
            clearPendingModal();
            setModal(null);
            router.invalidate();
          }}
        />
      )}

      {modal && modal.type === "standalone-distribution-code" && (
        <StandaloneDistributionCodeModal
          data={modal.data}
          client={client}
          onClose={() => {
            clearPendingModal();
            setModal(null);
            router.invalidate();
          }}
        />
      )}

      {modal && modal.type === "charge-completed" && (
        <ChargeCompleteModal
          onClose={() => {
            clearPendingModal();
            setModal(null);
            router.invalidate();
          }}
        />
      )}

      {modal && modal.type === "kyoto-representative-campaign-completed" && (
        <KyotoRepresentativeCampaignCompleteModal
          amount={modal.data.amount}
          onClose={() => {
            clearPendingModal();
            setModal(null);
            router.invalidate();
          }}
        />
      )}
    </>
  );
}
